import React, { useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import { makeStyles } from "@material-ui/core/styles";
import AccessTimeIcon from "@material-ui/icons/AccessTime";
import { getTokens } from "../../styles/tokens";
import { i18n } from "../../translate/i18n";

/* ------------------------------------------------------------------ */
/* Cálculo puro (espelha backend SlaCalculator)                        */
/* ------------------------------------------------------------------ */

const toMs = (d) => {
  if (!d) return null;
  const n = d instanceof Date ? d.getTime() : new Date(d).getTime();
  return Number.isFinite(n) ? n : null;
};

/**
 * Regra do doc: sem meta → null; respondido/finalizado dentro → ok;
 * ainda aberto e antes do limite mas ≥ warnPct → warning; passou → breached.
 * Devolve { status, elapsedSec, limitSec, remainingSec, done }.
 */
export const computeTarget = ({ startMs, endMs, nowMs, limitMin, warnPct }) => {
  const limit = Number(limitMin);
  if (!startMs || !Number.isFinite(limit) || limit <= 0) return null;
  const limitSec = Math.round(limit * 60);
  const done = !!endMs;
  const elapsedSec = Math.max(0, Math.floor(((done ? endMs : nowMs) - startMs) / 1000));
  const remainingSec = limitSec - elapsedSec;
  const pct = Number.isFinite(Number(warnPct)) ? Number(warnPct) : 80;
  let status;
  if (elapsedSec > limitSec) status = "breached";
  else if (!done && elapsedSec >= (limitSec * pct) / 100) status = "warning";
  else status = "ok";
  return { status, elapsedSec, limitSec, remainingSec, done };
};

/**
 * A partir do ticket (ticketTraking + queue.sla*) devolve o alvo "ativo":
 * 1ª resposta enquanto não houve resposta humana; depois resolução (se houver meta).
 * Devolve null quando não há SLA aplicável.
 */
export const computeTicketSla = (ticket, now = Date.now()) => {
  if (!ticket || ticket.status === "closed") return null;
  const traking = ticket.ticketTraking;
  const queue = ticket.queue;
  if (!traking || !queue) return null;

  const startMs = toMs(traking.enqueuedAt);
  if (!startMs) return null;

  const firstMin = queue.slaFirstResponseMinutes;
  const resolMin = queue.slaResolutionMinutes;
  const warnPct = queue.slaWarningPercent;
  const firstResponseMs = toMs(traking.firstResponseAt);
  const finishedMs = toMs(traking.finishedAt);

  const first = computeTarget({ startMs, endMs: firstResponseMs, nowMs: now, limitMin: firstMin, warnPct });
  const resolution = computeTarget({ startMs, endMs: finishedMs, nowMs: now, limitMin: resolMin, warnPct });

  let active = null;
  let kind = null;
  if (first && !first.done) {
    active = first;
    kind = "firstResponse";
  } else if (resolution) {
    active = resolution;
    kind = "resolution";
  } else if (first) {
    active = first;
    kind = "firstResponse";
  }
  if (!active) return null;

  const breached =
    (first && first.status === "breached") || (resolution && resolution.status === "breached");

  return {
    kind,
    status: active.status,
    remainingSec: active.remainingSec,
    limitSec: active.limitSec,
    done: active.done,
    breached: !!breached,
    first,
    resolution,
  };
};

const pad = (n) => String(n).padStart(2, "0");

/** Restante em `mm:ss` (ou `Xh MMm` acima de 1 h); estourado em `+Xm` / `+Xh Ym`. */
export const formatSlaTime = (remainingSec) => {
  const sec = Math.round(Number(remainingSec) || 0);
  if (sec < 0) {
    const over = -sec;
    const h = Math.floor(over / 3600);
    const m = Math.floor((over % 3600) / 60);
    if (h > 0) return `+${h}h ${pad(m)}m`;
    return `+${Math.max(1, m)}m`;
  }
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  if (h > 0) return `${h}h ${pad(m)}m`;
  return `${pad(m)}:${pad(s)}`;
};

/** `mm:ss` simples para durações (painel). */
export const formatDuration = (totalSec) => {
  const sec = Math.max(0, Math.round(Number(totalSec) || 0));
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  if (h > 0) return `${h}h ${pad(m)}m`;
  return `${pad(m)}:${pad(s)}`;
};

/* ------------------------------------------------------------------ */
/* Hook: recalcula a cada 30 s enquanto houver SLA em andamento         */
/* ------------------------------------------------------------------ */

const hasSlaConfig = (ticket) => {
  const q = ticket && ticket.queue;
  return !!(q && (q.slaFirstResponseMinutes || q.slaResolutionMinutes)) && !!(ticket.ticketTraking && ticket.ticketTraking.enqueuedAt);
};

export const useTicketSla = (ticket, intervalMs = 30000) => {
  const [now, setNow] = useState(() => Date.now());
  const enabled = hasSlaConfig(ticket) && ticket.status !== "closed";
  const ticketId = ticket ? ticket.id : null;
  const enqueuedAt = ticket && ticket.ticketTraking ? ticket.ticketTraking.enqueuedAt : null;

  useEffect(() => {
    if (!enabled) return undefined;
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(id);
  }, [enabled, intervalMs, ticketId, enqueuedAt]);

  return useMemo(() => (enabled ? computeTicketSla(ticket, now) : null), [enabled, ticket, now]);
};

/* ------------------------------------------------------------------ */
/* Chip                                                                */
/* ------------------------------------------------------------------ */

const useStyles = makeStyles((theme) => {
  const t = getTokens(theme.palette.type, theme.palette.primary.main);
  const dark = t.mode === "dark";
  const tone = (hex) => ({
    color: hex,
    backgroundColor: `${hex}${dark ? "2e" : "1a"}`,
  });
  return {
    root: {
      flex: "none",
      display: "inline-flex",
      alignItems: "center",
      gap: 3,
      height: 18,
      padding: "0 6px",
      borderRadius: t.radius.badge,
      fontSize: 11,
      fontWeight: 600,
      lineHeight: "18px",
      fontFamily: t.font,
      fontVariantNumeric: "tabular-nums",
      whiteSpace: "nowrap",
      "& svg": { fontSize: 12 },
    },
    ok: tone(t.semantic.success),
    warning: tone(t.semantic.warning),
    breached: tone(t.semantic.error),
  };
});

/**
 * Chip de SLA de um ticket: relógio + tempo restante (`mm:ss`) ou estourado (`+Xm`).
 * Cor por status. Não renderiza nada quando o ticket não tem SLA.
 * `sla` opcional: resultado de `useTicketSla` já calculado pelo pai (evita 2º timer).
 */
const SlaChip = ({ ticket, sla: slaProp, className }) => {
  const classes = useStyles();
  const own = useTicketSla(slaProp === undefined ? ticket : null);
  const sla = slaProp === undefined ? own : slaProp;

  if (!sla) return null;

  const kindLabel = i18n.t(`sla.kind.${sla.kind}`);
  const statusLabel = i18n.t(`sla.status.${sla.status}`);
  const text = sla.done && sla.status === "ok" ? statusLabel : formatSlaTime(sla.remainingSec);

  return (
    <span
      className={clsx(classes.root, classes[sla.status] || classes.ok, className)}
      title={`${kindLabel} · ${statusLabel}`}
    >
      <AccessTimeIcon />
      {text}
    </span>
  );
};

export default SlaChip;
